import { defineMessages } from "react-intl";
import { JiraServiceManagementPricingPageProps } from "src/pages/software/jira/service-management/pricing/jira-service-management-pricing-page";

import {
    PriceFinderMessages,
    ProductPlan
} from "@/components/wpl/pricing/src/price-finder/constants";
import PriceFinderSection from "@/components/wpl/pricing/src/price-finder/price-finder";
import { PriceFinderContextProvider } from "@/components/wpl/pricing/src/price-finder/price-finder-context.tsx";
import { createPlanFeatureMessages } from "@/components/wpl/pricing/src/price-finder/utils/create-price-finder-features.tsx";
import { usePricingPlanComparison } from "@/components/wpl/pricing/src/pricing-content-context.tsx";
import Disclaimer from "@/components/wpl/pricing/src/pricing-disclaimer/disclaimer";
// eslint-disable-next-line complexity
export const JsmPriceFinder = (
    props: JiraServiceManagementPricingPageProps
) => {
    const messages: PriceFinderMessages = defineMessages({
        headerText: {
            id: "JsmPricingPageV2.priceFinder.headerText",
            defaultMessage: "Service management <blue>that scales</blue>"
        },
        unitCountInputTextRedesign: {
            id: "JsmPricingPageV2.priceFinder.unitCountInputTextRedesign",
            defaultMessage: "Team size:"
        },
        unitCountInputDirectBuy: {
            id: "JsmPricingPageV2.priceFinder.unitCountInputDirectBuy",
            defaultMessage: "What's your team size?"
        },
        unitCountInputTerm: {
            id: "JsmPricingPageV2.priceFinder.unitCountInputTerm",
            defaultMessage: "agents"
        },
        billingCycleText: {
            id: "JsmPricingPageV2.priceFinder.billingCycleText",
            defaultMessage: "Billing cycle:"
        },
        unitCountInputText: {
            id: "JsmPricingPageV2.priceFinder.unitCountInputText",
            defaultMessage: "How many agents are on your team?"
        },
        unitCountInputTooltip: {
            id: "JsmPricingPageV2.priceFinder.unitCountInputTooltip",
            defaultMessage:
                "Agents work on tickets and communicate directly with your customers."
        },
        cardOverSignupLimit: {
            id: "JsmPricingPageV2.priceFinder.cardOverSignupLimit",
            defaultMessage: "Your agent count exceeds this plan's limit"
        },
        cardEnterpriseMinimumCount: {
            id: "JsmPricingPageV2.priceFinder.cardEnterpriseMinimumCount",
            defaultMessage:
                "Enterprise plan pricing is available when you enter {enterpriseMinimumCount} or more agents above",
            description:
                "Enterprise is a plan name and should not be translated"
        },
        cardUnitCountTierRange: {
            id: "JsmPricingPageV2.priceFinder.cardUnitCountTierRange",
            defaultMessage: "Agent tier: {lowerLimit} - {upperLimit}"
        },
        cardMonthlyPerUnitCount: {
            id: "JsmPricingPageV2.priceFinder.cardMonthlyPerUnitCount",
            defaultMessage: "per agent / month"
        },
        cardFreeDescription: {
            id: "JsmPricingPageV2.priceFinder.cardFreeDescription",
            defaultMessage: "always free for 3 agents,"
        }
    });

    const content = {
        free: usePricingPlanComparison("Free"),
        standard: usePricingPlanComparison("Standard"),
        premium: usePricingPlanComparison("Premium"),
        enterprise: usePricingPlanComparison("Enterprise")
    };

    const freePlanFeatureMessages = createPlanFeatureMessages(content, "free");
    const standardPlanFeatureMessages = createPlanFeatureMessages(
        content,
        "standard"
    );
    const premiumPlanFeatureMessages = createPlanFeatureMessages(
        content,
        "premium"
    );
    const enterprisePlanFeatureMessages = createPlanFeatureMessages(
        content,
        "enterprise"
    );
    // Note: We need to use the jiraServiceDesk plan name instead of a new jiraServiceManagement key because of how the pricing data is stored in CCP/HAMS. jiraServiceManagement is used as the dataProductKey in all other components on the JSM pricing page.
    const jiraServiceManagementProductPlans: ProductPlan[] = [
        {
            planName: content.free?.planTitleExternal || "Free",
            offeringSku: "jira-servicedesk-free",
            description: content.free?.planOneLiner || "",
            recommendationLimit: 0,
            signupLimit: 20000,
            signupURLSuffix:
                "?bundle=jira-service-management&edition=free&signupSource=pricing",
            planFeatureMessages: freePlanFeatureMessages
        },
        {
            planName: content.standard?.planTitleExternal || "Standard",
            offeringSku: "jira-servicedesk-standard-repack-sku",
            description: content.standard?.planOneLiner || "",
            recommendationLimit: 3,
            signupLimit: 20000,
            signupURLSuffix: "?bundle=jira-service-management&edition=standard",
            planFeatureMessages: standardPlanFeatureMessages
        },
        {
            planName: content.premium?.planTitleExternal || "Premium",
            offeringSku: "jira-servicedesk-premium",
            description: content.premium?.planOneLiner || "",
            recommendationLimit: 50,
            signupLimit: 20000,
            signupURLSuffix: "?bundle=jira-service-management&edition=premium",
            planFeatureMessages: premiumPlanFeatureMessages
        },
        {
            planName: content.enterprise?.planTitleExternal || "Enterprise",
            offeringSku: "jira-servicedesk-enterprise-sku",
            recommendationLimit: 250,
            description: content.enterprise?.planOneLiner || "",
            signupLimit: 20000,
            planFeatureMessages: enterprisePlanFeatureMessages
        }
    ];

    return (
        <PriceFinderContextProvider
            dataProductKey="jiraServiceDesk"
            productPlans={jiraServiceManagementProductPlans}
            defaultUnitCount={75}
            enterpriseMinimumCount={201}
            shouldAddBuyNow={true}
        >
            <PriceFinderSection
                messages={messages}
                serverSideCCPPricing={
                    props.pricing?.ccpPricingData?.jiraServiceDesk
                }
                countryCode={props.countryCode}
            />
            <Disclaimer />
        </PriceFinderContextProvider>
    );
};
JsmPriceFinder.displayName = "JsmPriceFinderSection";
